// i18n
import { translateInComponent } from 'infras/i18n';
import translations from '../i18n.json';

import { useContext, useState } from 'react';
import { useRouter } from 'next/router';

import {
  DashboardPortalContext,
  setErrorMessage,
} from 'presentations/reducers/DashboardPortal';
import { UserListItem, ViewerItem } from 'presentations/api/schema/user';
import {
  TaskDetailListItem,
  TaskUpdateBody,
} from 'presentations/api/schema/task';
import { apiPath } from 'presentations/routes/path';
import { useRequest } from 'presentations/views/hooks/request';

import { patch } from 'infras/rest_client';

import { TaskStatus } from 'domains/models/task/status';
import { formatToMills, getDateTime, dateTimeToSQL } from 'infras/datetime';

export interface Props {
  viewer: ViewerItem;
  tasks?: Array<TaskDetailListItem>;
  listEndpoint?: string;
  taskableType?: string;
  taskableId?: number | string;
  teamUsers?: UserListItem[];
  isDisabled?: boolean;
}

export const useTaskList = (props: Props) => {
  const t = translateInComponent(translations);

  const router = useRouter();
  const { dispatch } = useContext(DashboardPortalContext);

  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [isAddTask, setIsAddTask] = useState<boolean>(false);
  const [isRequesting, setIsRequesting] = useState<boolean>(false);
  const [selectedTask, setSelectedTask] = useState<TaskDetailListItem | null>(
    null,
  );
  const [deleteTask, setDeleteTask] = useState<TaskDetailListItem | null>(null);

  const now = getDateTime();
  const startedAt = formatToMills(now.startOf('day'));
  const endedAt = formatToMills(now.endOf('day'));
  const listEndpoint =
    props.listEndpoint ||
    (props.viewer.team && !props.isDisabled
      ? `/v1/teams/${props.viewer.team.uid}/users/${props.viewer.uid}/tasks`
      : null);

  const tasksRequest = useRequest(
    listEndpoint
      ? apiPath(listEndpoint, { started: startedAt, ended: endedAt })
      : null,
  );
  const tasksData = tasksRequest.data || props.tasks;
  const allTasks: TaskDetailListItem[] = tasksData || [];
  const currentDayStart = getDateTime().startOf('day').toMillis();
  const tasks = allTasks.filter(
    (task) => formatToMills(task.endedAt) >= currentDayStart,
  );
  const carryOverTasks = allTasks.filter(
    (task) =>
      task.endedAt === null || formatToMills(task.endedAt) < currentDayStart,
  );

  const onClickDone = async (task: TaskDetailListItem) => {
    setIsRequesting(true);
    const updateStatus =
      task.status === TaskStatus.None ? TaskStatus.Done : TaskStatus.None;
    const endedAt = getDateTime();
    const newTasks = allTasks.map((t) => {
      if (t.uid === task.uid) {
        const newTask = {
          ...t,
          status: updateStatus,
          doneAt: dateTimeToSQL(endedAt),
          endedAt: dateTimeToSQL(endedAt),
        };
        return newTask;
      }
      return t;
    });
    await tasksRequest.mutate(() => {
      return { ...tasksRequest, data: newTasks };
    }, false);

    const data: TaskUpdateBody = {
      status:
        task.status === TaskStatus.None ? TaskStatus.Done : TaskStatus.None,
    };
    const result = await patch(apiPath(`/v1/tasks/${task.uid}/update`), {
      data,
    });

    setIsRequesting(false);
    if (!result.isSuccess) {
      dispatch(setErrorMessage(t('Done Error')));
      await tasksRequest.mutate(() => {
        return { ...tasksRequest, data: allTasks };
      }, false);
      return;
    }
  };

  return {
    state: {
      isDragging,
      isAddTask,
      isTaskLoading: !tasksData,
      isRequesting,
      tasks,
      carryOverTasks,
      deleteTask,
      selectedTask,
    },
    actions: {
      onClickDone,
    },
  };
};
